import React, { useMemo } from 'react';

import { Stack } from '@eluve/components';

import { availableInterstitials } from '../InterstitialsProvider';
import { useInterstitials } from '../hooks/useInterstitials';
import { BaseInterstitial, InterstitialContext } from '../types';

export const InterstitialSidePanels: React.FC = () => {
  const { context, isLoading } = useInterstitials();

  const shouldShowSidePanel = (
    interstitial: BaseInterstitial,
    interstitialContext: InterstitialContext,
  ): boolean => {
    if (!interstitial.sidepanelComponent) {
      return false;
    }

    if (interstitial.showOnPaths && interstitial.showOnPaths.length > 0) {
      const matchesPath = interstitial.showOnPaths.some((pathRegex) =>
        interstitialContext.currentPath?.match(pathRegex),
      );
      if (!matchesPath) {
        return false;
      }
    }

    if (interstitial.shouldShowSidePanel) {
      return interstitial.shouldShowSidePanel(interstitialContext);
    }

    // Fallback to standard shouldShow logic for interstitials without custom side panel logic
    return interstitial.shouldShow(interstitialContext);
  };

  const eligibleSidePanels = useMemo(() => {
    if (isLoading || !context) {
      return [];
    }

    return availableInterstitials
      .filter((interstitial) => shouldShowSidePanel(interstitial, context))
      .sort((a, b) => b.priority - a.priority);
  }, [context, isLoading]);

  if (isLoading || !context || eligibleSidePanels.length === 0) {
    return null;
  }

  return (
    <Stack gap={2}>
      {eligibleSidePanels.map((interstitial) => {
        const SidePanelComponent = interstitial.sidepanelComponent;
        if (!SidePanelComponent) {
          return null;
        }
        return <SidePanelComponent key={interstitial.name} context={context} />;
      })}
    </Stack>
  );
};
